import type { IntlShape } from "react-intl";
import { ROW_HEIGHT, TOTAL_ROWS } from "@/constants/appointments/appointments-constants";
import { OpeningHours, CalendarBlock } from "@/types/appointments/calendar-types";
import type { TimeRange } from "@/types/appointments/api-types/availability-types";


export const getTimeLabel = (intl: IntlShape, rowIndex: number): string | null => {
  if (rowIndex % 4 === 1) {
    return intl.formatTime(new Date(2000, 0, 1, Math.floor(rowIndex / 4)), { format: "hour" });
  }
  return null;
};

export const isOddHour = (rowIndex: number, openingHours: OpeningHours): boolean => {
  const hourIndex = Math.floor(rowIndex / 4);
  const relativeHourIndex = openingHours ? hourIndex - openingHours.start : hourIndex;
  return relativeHourIndex % 2 === 1;
};

export const isWithinWorkingHours = (
  rowIndex: number,
  working_hours: TimeRange[]
): boolean => {
  if (working_hours.length === 0) return false;
  return working_hours.some((range) => {
    const start = parseStartTime(range.start_time);
    const end = parseStartTime(range.end_time);
    const startRow = timeToRowIndex(start.hour, start.minute);
    const endRow = timeToRowIndex(end.hour, end.minute);
    return rowIndex >= startRow && rowIndex < endRow;
  });
};

export const workingHoursToOpeningHours = (
  working_hours: TimeRange[]
): OpeningHours => {
  if (working_hours.length === 0) {
    return { start: 0, end: 0 };
  }

  let minStartMinutes = Infinity;
  let maxEndMinutes = -Infinity;
  for (const range of working_hours) {
    const start = parseStartTime(range.start_time);
    const end = parseStartTime(range.end_time);
    minStartMinutes = Math.min(minStartMinutes, start.hour * 60 + start.minute);
    maxEndMinutes = Math.max(maxEndMinutes, end.hour * 60 + end.minute);
  }

  return {
    start: Math.floor(minStartMinutes / 60),
    end: Math.ceil(maxEndMinutes / 60),
  };
};

export const parseStartTime = (
  start_time: string
): { hour: number; minute: number } => {
  const [hour, minute] = start_time.split(":");
  return { hour: Number(hour) || 0, minute: Number(minute) || 0 };
};

export const timeToRowIndex = (hour: number, minute: number = 0): number => {
  const hourRows = hour * 4;
  const minuteRows = Math.floor(minute / 15);
  return hourRows + minuteRows;
};

export const rowIndexToTime = (rowIndex: number): { hour: number; minute: number } => {
  const hour = Math.floor(rowIndex / 4);
  const minute = (rowIndex % 4) * 15;
  return { hour, minute };
};

export const formatSlotTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

const timeToMinutes = (time: string): number => {
  const { hour, minute } = parseStartTime(time);
  return hour * 60 + minute;
};

/** True when [start, start+duration) extends past midnight (1440). End at 1440 fits. */
export const lineEndsAfterMidnight = (
  startTime: string,
  durationMinutes: number
): boolean => {
  return timeToMinutes(startTime) + durationMinutes > TOTAL_ROWS * 15;
};

/** True when [startA, startA+durationA) overlaps [startB, startB+durationB). Adjacent is false. */
export const rangesOverlap = (
  startA: string,
  durationA: number,
  startB: string,
  durationB: number
): boolean => {
  const startAMinutes = timeToMinutes(startA);
  const endAMinutes = startAMinutes + durationA;
  const startBMinutes = timeToMinutes(startB);
  const endBMinutes = startBMinutes + durationB;
  return startAMinutes < endBMinutes && startBMinutes < endAMinutes;
};

// Walk the same 15-minute grid as the calendar:
// - snap a range start *up* to the next 15-min mark (09:10–10:00 → 09:15, not 09:00)
// - end is exclusive (09:00–10:00 → 09:00, 09:15, 09:30, 09:45 — not 10:00)
// - optional durationMinutes: keep start S only if S + duration <= that range's end
//   (do not span a split-shift / lunch gap). Omit or non-positive → every grid start.
export const availableSlotsToStartTimes = (
  slots: TimeRange[],
  durationMinutes?: number
): string[] => {
  const times = new Set<string>();
  const duration =
    typeof durationMinutes === "number" && durationMinutes > 0
      ? durationMinutes
      : 0;
  for (const range of slots) {
    let current = timeToMinutes(range.start_time);
    const end = timeToMinutes(range.end_time);
    const remainder = current % 15;
    if (remainder !== 0) {
      current += 15 - remainder;
    }
    while (current < end) {
      if (duration === 0 || current + duration <= end) {
        const hour = Math.floor(current / 60);
        const minute = current % 60;
        times.add(formatSlotTime(hour, minute));
      }
      current += 15;
    }
  }
  return [...times].sort();
};

export const durationToRows = (durationMinutes: number): number => {
  return Math.ceil(durationMinutes / 15);
};

export const formatTime = (intl: IntlShape, hour: number, minute: number = 0): string =>
  intl.formatTime(new Date(2000, 0, 1, hour, minute), { format: "clock" });

export const pixelsToRowDelta = (deltaY: number): number => {
  return Math.round(deltaY / ROW_HEIGHT);
};

export const clampRowIndex = (rowIndex: number): number => {
  return Math.max(0, Math.min(TOTAL_ROWS - 1, rowIndex));
};

export const rowsToMinutes = (rows: number): number => {
  return rows * 15;
};

export const calculateResizedDurationRows = (
  deltaY: number,
  originalDurationRows: number,
  startRow: number
): number => {
  const rowDelta = pixelsToRowDelta(deltaY);
  const maxRows = TOTAL_ROWS - startRow;
  return Math.max(1, Math.min(maxRows, originalDurationRows + rowDelta));
};

export const calculateInitialScrollPosition = (
  openingHours: OpeningHours,
  appointments: CalendarBlock[] = []
): number => {
  const openingRow = openingHours.start * 4;

  if (appointments.length === 0) {
    return openingRow * ROW_HEIGHT;
  }

  const earliestAppointment = appointments.reduce((earliest, appointment) => {
    const current = parseStartTime(appointment.start_time);
    const earliestTime = parseStartTime(earliest.start_time);
    const currentRow = timeToRowIndex(current.hour, current.minute);
    const earliestRow = timeToRowIndex(earliestTime.hour, earliestTime.minute);
    return currentRow < earliestRow ? appointment : earliest;
  });

  const earliestTime = parseStartTime(earliestAppointment.start_time);
  const earliestAppointmentRow = timeToRowIndex(
    earliestTime.hour,
    earliestTime.minute
  );

  const targetRow = Math.min(earliestAppointmentRow, openingRow);
  return targetRow * ROW_HEIGHT;
};